import React, { useCallback, useMemo } from "react";

import { useI18n } from "../../../../hooks/i18n-hooks";

import VStack from "../../../../ui_atoms/VStack";
import ScreenCard, { ButtonType } from "../../../../screens/ScreenCard";
import { BodyMediumText, LinkSmallTextBold, TitleMediumText } from "../../../FormattedText";

import { StackScreenProps } from "@react-navigation/stack";
import { IJMEditorStackParamList } from "../IJMEditorNavigator";
import { useRecoilState, useRecoilValue } from "recoil";
import { editIjmSelection, editIjmSelectionCallback } from "../../../../atoms/core";
import { BubbleTalk } from "../../../BubbleTalk";
import DocuIllSvg from "../../../../assets/images/docu_ill.svg";
import { useQuery } from "@apollo/client";
import { GET_IJM_CHOICES } from "../../../../graphql/client";
import {
    getPayRemainingPercentsSetAndLabel,
    getPayFirstDaysSetAndLabel,
    isSelectionValid,
} from "../helper";
import ActionButton from "../../../ActionButton";
import { useTheme } from "../../../../hooks/use-theme";
import Gap from "../../../../ui_atoms/Gap";
import Separator from "../../../../ui_atoms/separator";
import AutoSizeImage from "../../../AutoSizeImage";
import { useIntercom } from "../../../../hooks/intercom";

type IJMRootScreenProps = StackScreenProps<IJMEditorStackParamList, "IJM">;

const IJMRootScreen: React.FC<IJMRootScreenProps> = ({ navigation }) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const [editorCallback, setEditorCallback] = useRecoilState(editIjmSelectionCallback);
    const selection = useRecoilValue(editIjmSelection);
    const isValid = useMemo(() => isSelectionValid(selection), [selection]);
    const { presentArticle } = useIntercom();
    const onSave = useCallback(async () => {
        await editorCallback?.func?.("save");
        setEditorCallback({});
    }, [editorCallback, setEditorCallback]);

    const onCancel = useCallback(async () => {
        await editorCallback?.func?.("cancel");
        setEditorCallback({});
    }, [editorCallback, setEditorCallback]);

    const { data } = useQuery(GET_IJM_CHOICES, {
        fetchPolicy: "cache-and-network",
    });
    const currentGroup = useMemo(() => {
        return (data?.getIjmChoices || []).find((ijmChoice) => {
            return ijmChoice.id === selection.ijmGroupId;
        });
    }, [data?.getIjmChoices, selection.ijmGroupId]);

    const buttons = useMemo<ButtonType[]>(() => {
        return [
            {
                title: i18n.t("save"),
                onPress: onSave,
                mode: "secondary",
                isValid: isValid,
            },
            {
                title: i18n.t("cancel"),
                onPress: onCancel,
                mode: "soft",
                isValid: true,
            },
        ];
    }, [i18n, onSave, isValid, onCancel]);

    const [ijmPayFirstDaysSet, ijmPayFirstDaysText] = useMemo(() => {
        return getPayFirstDaysSetAndLabel(selection.ijmPayFirstDays);
    }, [selection.ijmPayFirstDays]);

    const [ijmPayRemainingPercentSet, ijmPayRemainingPercentText] = useMemo(() => {
        return getPayRemainingPercentsSetAndLabel(selection.ijmPayRemainingPercents);
    }, [selection.ijmPayRemainingPercents]);

    const editable = true; // Always editable currently

    return (
        <ScreenCard buttons={buttons}>
            <VStack>
                <VStack h="2/3">
                    <Gap gap={0.5} />
                    <BubbleTalk
                        personImageComponent={<DocuIllSvg width="100%" height="100%" />}
                        personSize={44}
                        position="top-center"
                    >
                        <TitleMediumText>{i18n.t("ijm.editor.root.title")}</TitleMediumText>
                    </BubbleTalk>
                    <Gap gap={1} />
                    <BodyMediumText>{i18n.t("ijm.editor.root.text_1")}</BodyMediumText>
                    <LinkSmallTextBold
                        onPress={() => {
                            void presentArticle("IJMGroupClient");
                        }}
                    >
                        {i18n.t("know_more")}
                    </LinkSmallTextBold>
                </VStack>
                <Separator v={1 / 2} />

                <VStack h="2/3">
                    {/* Group select */}
                    <TitleMediumText>{i18n.t("ijm.editor.root.title_2")}</TitleMediumText>
                    <Gap gap={0.5} />

                    <BodyMediumText>1. {i18n.t("ijm.editor.group_select.title")}</BodyMediumText>
                    <Gap gap={1 / 4} />
                    <ActionButton
                        text={
                            currentGroup ? currentGroup.name : i18n.t("ijm.ijm_group.place_holder")
                        }
                        textColor={currentGroup ? colors.text : colors.greyAccessible}
                        textWidth={currentGroup ? "bold" : "medium"}
                        rightIcon={
                            editable
                                ? {
                                      name: "chevron-down",
                                      color: colors.text,
                                  }
                                : undefined
                        }
                        onPress={() => {
                            navigation.navigate("GroupSelect");
                        }}
                    />
                    <Gap gap={1} />

                    {/* Waiting period select */}
                    <BodyMediumText>2. {i18n.t("ijm.editor.waiting_select.title")}</BodyMediumText>
                    <Gap gap={1 / 4} />
                    <ActionButton
                        text={i18n.t(ijmPayFirstDaysText)}
                        textColor={ijmPayFirstDaysSet ? colors.text : colors.greyAccessible}
                        textWidth={ijmPayFirstDaysSet ? "bold" : "medium"}
                        rightIcon={
                            editable
                                ? {
                                      name: "chevron-down",
                                      color: colors.text,
                                  }
                                : undefined
                        }
                        onPress={() => navigation.navigate("WaitingSelect")}
                    />
                    <Gap gap={1} />

                    {/* First days coverage */}
                    <BodyMediumText>3. {i18n.t("ijm.editor.cover_select.title")}</BodyMediumText>
                    <Gap gap={1 / 4} />
                    <ActionButton
                        text={i18n.t(ijmPayRemainingPercentText)}
                        textColor={ijmPayRemainingPercentSet ? colors.text : colors.greyAccessible}
                        textWidth={ijmPayRemainingPercentSet ? "bold" : "medium"}
                        numberOfLines={3}
                        rightIcon={
                            editable
                                ? {
                                      name: "chevron-down",
                                      color: colors.text,
                                  }
                                : undefined
                        }
                        onPress={() => navigation.navigate("CoverSelect")}
                    />
                    <VStack alignment="center" style={{ opacity: 0.5 }}>
                        <Gap gap={3} />
                        <BodyMediumText>{i18n.t("ijm.editor.root.collaboration")}</BodyMediumText>
                        <Gap gap={1 / 2} />
                        <AutoSizeImage
                            source={require("../../../../assets/images/groupe_mutuel.png")}
                            fixedHeight={40}
                        />
                    </VStack>
                </VStack>
            </VStack>
        </ScreenCard>
    );
};

export default IJMRootScreen;
